import React, { useState, useEffect, useCallback } from 'react';
import { X, Hash, Percent, Layers, Save, SkipForward } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { fieldRegistrationService } from '../../services/fieldRegistrationService';
import { itemBinSizeService } from '../../services/itemBinSizeService';
import { EditableItemCapacity } from '../../types/fieldRegistration';

// ── Types ──────────────────────────────────────────────────────

export interface CapacityUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (newMaxQuantity: number) => void | Promise<void>;

  /** Item number */
  itemNo: string;
  /** Item description (optional, for display) */
  itemDescription?: string;

  /** Bin / location code */
  binCode: string;
  /** BinSizeNo from StcBin (determines which ItemBinSize row) */
  binSizeNo: number;

  /** Current quantity on location (from ItemBins / StcTrans) */
  currentQuantity?: number;
  /** Existing MaxQuantity in ItemBinSize (null = no config yet) */
  currentMaxQuantity?: number;
  /** Existing ItemBinSize row ID (null = create new) */
  existingItemBinSizeId?: number | null;
  /** Existing putAwayPriorityNo to preserve */
  existingPutAwayPriorityNo?: number;

  /**
   * Optional override of the persist step. When provided, the modal calls this instead of
   * writing via fieldRegistrationService (e.g. consolidation routes through the
   * capacity-feedback endpoint to set provenance + raise a domain event). onSaved still fires
   * afterwards. When omitted, the default field-registration write path is used (put-away).
   */
  saveOverride?: (newMaxQuantity: number) => Promise<void>;
}

type InputMode = 'direct' | 'percent' | 'multiplier';

// ── Component ──────────────────────────────────────────────────

export const CapacityUpdateModal: React.FC<CapacityUpdateModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  itemNo,
  itemDescription,
  binCode,
  binSizeNo,
  currentQuantity,
  currentMaxQuantity,
  existingItemBinSizeId,
  existingPutAwayPriorityNo,
  saveOverride,
}) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // ── State ──
  const [mode, setMode] = useState<InputMode>('direct');
  const [directValue, setDirectValue] = useState<string>('');
  const [percentValue, setPercentValue] = useState<string>('');
  const [multiplier, setMultiplier] = useState<number | null>(null);
  const [customMultiplier, setCustomMultiplier] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Derived: computed maxQuantity ──
  const computedMaxQuantity = useCallback((): number | null => {
    const qty = currentQuantity ?? 0;

    if (mode === 'direct') {
      const val = parseInt(directValue, 10);
      return isNaN(val) || val <= 0 ? null : val;
    }

    if (mode === 'percent') {
      const pct = parseFloat(percentValue);
      if (isNaN(pct) || pct <= 0 || pct > 100 || qty <= 0) return null;
      return Math.round(qty * (100 / pct));
    }

    if (mode === 'multiplier') {
      const m = multiplier ?? parseFloat(customMultiplier);
      if (isNaN(m) || m <= 0 || qty <= 0) return null;
      return Math.round(qty * m);
    }

    return null;
  }, [mode, directValue, percentValue, multiplier, customMultiplier, currentQuantity]);

  const newMax = computedMaxQuantity();

  // ── Reset state on open ──
  useEffect(() => {
    if (isOpen) {
      setMode('direct');
      setDirectValue(currentMaxQuantity ? String(currentMaxQuantity) : '');
      setPercentValue('');
      setMultiplier(null);
      setCustomMultiplier('');
      setSaving(false);
      setError(null);
    }
  }, [isOpen, currentMaxQuantity]);

  // ── Escape to close ──
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // ── Save handler ──
  const handleSave = async () => {
    if (newMax === null) return;
    // Default (field-registration) path needs companyNo; the override path does not.
    if (!saveOverride && !companyNo) return;

    setSaving(true);
    setError(null);

    try {
      if (saveOverride) {
        await saveOverride(newMax);
      } else {
        const capacity: EditableItemCapacity = {
          maxQuantity: newMax,
          putAwayPriorityNo: existingPutAwayPriorityNo ?? 1,
        };

        await fieldRegistrationService.updateItemCapacity(
          companyNo,
          itemNo,
          binSizeNo,
          existingItemBinSizeId ?? null,
          capacity
        );
      }

      await onSaved(newMax);
    } catch (err) {
      setError((err as Error).message || 'Kunne ikke lagre kapasitet');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const qty = currentQuantity ?? 0;
  const fillPct = newMax && newMax > 0 ? Math.round((qty / newMax) * 100) : null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-[#0E7C86]/5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#0E7C86] flex items-center justify-center flex-shrink-0">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                  Oppdater kapasitet
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {itemNo}{itemDescription ? ` — ${itemDescription}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* ── Content ── */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

            {/* Info-kort */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs block">Lokasjon</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{binCode}</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs block">Saldo</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{qty} stk</span>
              </div>
              {currentMaxQuantity != null && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 col-span-2">
                  <span className="text-amber-600 dark:text-amber-400 text-xs block">Nåværende maks</span>
                  <span className="font-medium text-amber-800 dark:text-amber-300">{currentMaxQuantity} stk</span>
                </div>
              )}
            </div>

            {/* Mode-velger */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {([
                { key: 'direct' as InputMode, label: 'Antall', icon: Hash },
                { key: 'percent' as InputMode, label: 'Prosent', icon: Percent },
                { key: 'multiplier' as InputMode, label: 'Multiplier', icon: Layers },
              ]).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setMode(key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    mode === key
                      ? 'bg-white dark:bg-gray-600 text-[#0E7C86] dark:text-[#4fd1c5] shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* ── Mode A: Direkte antall ── */}
            {mode === 'direct' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hvor mange stk får plass?
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  autoFocus
                  value={directValue}
                  onChange={(e) => setDirectValue(e.target.value)}
                  placeholder="F.eks. 25"
                  className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0E7C86] focus:border-[#0E7C86] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  min="1"
                />
              </div>
            )}

            {/* ── Mode B: Prosent av saldo ── */}
            {mode === 'percent' && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hvor full er lokasjonen? (%)
                </label>
                {qty <= 0 ? (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Saldo er 0 — bruk direkte antall i stedet
                  </p>
                ) : (
                  <>
                    {/* Preset-knapper */}
                    <div className="grid grid-cols-4 gap-2">
                      {[25, 50, 75, 100].map((pct) => (
                        <button
                          key={pct}
                          onClick={() => setPercentValue(String(pct))}
                          className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                            percentValue === String(pct)
                              ? 'bg-[#0E7C86] text-white border-[#0E7C86]'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[#0E7C86]'
                          }`}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                    {/* Egendefinert */}
                    <input
                      type="number"
                      inputMode="numeric"
                      value={percentValue}
                      onChange={(e) => setPercentValue(e.target.value)}
                      placeholder="Egendefinert %"
                      className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0E7C86] focus:border-[#0E7C86] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      min="1"
                      max="100"
                    />
                    {percentValue && !isNaN(parseFloat(percentValue)) && parseFloat(percentValue) > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {qty} stk er {percentValue}% av lokasjonen → maks{' '}
                        <strong>{Math.round(qty * (100 / parseFloat(percentValue)))}</strong> stk
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Mode C: Multiplier ── */}
            {mode === 'multiplier' && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Plass til X ganger nåværende saldo
                </label>
                {qty <= 0 ? (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Saldo er 0 — bruk direkte antall i stedet
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-4 gap-2">
                      {[2, 3, 5, 10].map((m) => (
                        <button
                          key={m}
                          onClick={() => { setMultiplier(m); setCustomMultiplier(''); }}
                          className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                            multiplier === m && !customMultiplier
                              ? 'bg-[#0E7C86] text-white border-[#0E7C86]'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[#0E7C86]'
                          }`}
                        >
                          {m}x
                        </button>
                      ))}
                    </div>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={customMultiplier}
                      onChange={(e) => { setCustomMultiplier(e.target.value); setMultiplier(null); }}
                      placeholder="Egendefinert (f.eks. 1.5)"
                      className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0E7C86] focus:border-[#0E7C86] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      min="0.1"
                      step="0.1"
                    />
                    {(() => {
                      const m = multiplier ?? parseFloat(customMultiplier);
                      if (!isNaN(m) && m > 0) {
                        return (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {qty} stk × {m} = <strong>{Math.round(qty * m)}</strong> stk
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </>
                )}
              </div>
            )}

            {/* ── Preview / bekreftelse ── */}
            {newMax !== null && (
              <div className="bg-[#0E7C86]/5 dark:bg-[#0E7C86]/10 border border-[#0E7C86]/20 rounded-lg px-4 py-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Ny kapasitet</span>
                  <span className="text-lg font-bold text-[#0E7C86] dark:text-[#4fd1c5]">{newMax} stk</span>
                </div>
                {currentMaxQuantity != null && (
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Endring fra</span>
                    <span>{currentMaxQuantity} → {newMax} stk</span>
                  </div>
                )}
                {qty > 0 && (
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Fyllingsgrad</span>
                    <span>{fillPct}%</span>
                  </div>
                )}
                {/* Visual bar */}
                {fillPct !== null && (
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mt-1">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        fillPct > 90 ? 'bg-red-500' : fillPct > 70 ? 'bg-amber-500' : 'bg-[#0E7C86]'
                      }`}
                      style={{ width: `${Math.min(fillPct, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <SkipForward className="w-4 h-4" />
              Hopp over
            </button>
            <button
              onClick={handleSave}
              disabled={newMax === null || saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#0E7C86] rounded-lg hover:bg-[#0A5F66] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Lagrer...' : 'Lagre'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
